import React, { useState, useRef, useEffect } from 'react';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import logger from './../../utils/logger.js';

// Import step components
import CameraStep from './steps/CameraStep';
import WelcomeStep from './steps/WelcomeStep';
import MicrophoneStep from './steps/MicrophoneStep';
import ScreenShareStep from './steps/ScreenShareStep';
import GuidelinesStep from './steps/GuidelinesStep';
import BotInteractionStep from './steps/BotInteractionStep';

// Step indicators component
const StepIndicator = ({ currentStep, totalSteps }) => {
  return (
    <div className="flex justify-center mb-3 gap-2">
      {Array.from({ length: totalSteps }).map((_, index) => (
        <div
          key={index}
          className={`h-1 rounded-full transition-all duration-300 ${index === currentStep ? 'w-16 bg-indigo-600' : 'w-12 bg-gray-200'} ${index < currentStep ? 'bg-indigo-400' : ''}`}
        />
      ))}
    </div>
  );
};

// Enhanced audio level visualization hook with waveform data
const useAudioLevelMeter = (audioContext, analyser, dataArray) => {
  const [audioData, setAudioData] = useState({
    level: 0,
    waveform: Array(64).fill(0)  // Initialize with more points for smoother visualization
  });
  const animationRef = useRef(null);

  useEffect(() => {
    if (!analyser) return;

    const updateAudioLevel = () => {
      // Get frequency data for level calculation
      analyser.getByteFrequencyData(dataArray);

      // Calculate average level
      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        sum += dataArray[i];
      }
      const avg = sum / dataArray.length;
      const normalizedLevel = Math.min(1, avg / 128); // Normalize to 0-1

      // Get time domain data for waveform
      const waveformBuffer = new Uint8Array(analyser.fftSize);
      analyser.getByteTimeDomainData(waveformBuffer);

      // Process the waveform data to get a more visually appealing wave
      // Take more points for smoother visualization
      const waveformPoints = Array.from({ length: 64 }).map((_, i) => {
        const index = Math.floor(i * (waveformBuffer.length / 64));
        // Convert to a value between -1 and 1, where 128 is the center line
        return (waveformBuffer[index] - 128) / 128;
      });

      setAudioData({
        level: normalizedLevel,
        waveform: waveformPoints
      });

      animationRef.current = requestAnimationFrame(updateAudioLevel);
    };

    updateAudioLevel();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [analyser, dataArray]);

  return audioData;
};

const PermissionCheck = ({
  onComplete,
  startWithScreenSharing = false,
  interviewId = null,
  className = '',
  handleJoin,
  isActiveLoader,
  isMobile,
  enterFullScreen,
  socketData,
  permissionsGranted,
  interviewStatus,
  steps = [
    { id: 'welcome', component: WelcomeStep },
    { id: 'microphone', component: MicrophoneStep },
    { id: 'camera', component: CameraStep },
    { id: 'botInteraction', component: BotInteractionStep },
    { id: 'guidelines', component: GuidelinesStep }
    // Screen sharing step removed as requested
  ]
}) => {
  const TOTAL_STEPS = steps.length;

  // State
  const [currentStep, setCurrentStep] = useState(0);
  const [permissionStatus, setPermissionStatus] = useState({
    microphone: 'pending',
    camera: 'pending',
    faceDetection: 'pending',
    botInteraction: 'pending',
    screenShare: 'pending',
  });

  // Added state for guidelines acknowledgement
  const [guidelinesAcknowledged, setGuidelinesAcknowledged] = useState(false);

  // Refs
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const dataArrayRef = useRef(null);
  const mediaStreamRef = useRef(null);

  // Audio visualization
  const setupAudioContext = (stream) => {
    if (!stream) return;

    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = audioContext.createAnalyser();
      // Increase FFT size for more detailed analysis
      analyser.fftSize = 512;

      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;
      dataArrayRef.current = dataArray;
    } catch (error) {
      logger.error("Error setting up audio context:", error);
    }
  };

  const audioData = useAudioLevelMeter(
    audioContextRef.current,
    analyserRef.current,
    dataArrayRef.current
  );

  // Check microphone permission
  const checkMicrophonePermission = async () => {
    try {
      setPermissionStatus(prev => ({ ...prev, microphone: 'checking' }));

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Setup audio visualization
      setupAudioContext(stream);

      // Store the stream for later cleanup
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getAudioTracks().forEach(track => track.stop());
      }
      mediaStreamRef.current = stream;

      setPermissionStatus(prev => ({ ...prev, microphone: 'granted' }));
      return true;
    } catch (error) {
      logger.error("Microphone permission error:", error);
      setPermissionStatus(prev => ({ ...prev, microphone: 'denied' }));
      return false;
    }
  };

  // Check camera permission
  const checkCameraPermission = async () => {
    try {
      setPermissionStatus(prev => ({ ...prev, camera: 'checking' }));

      // Use simpler camera constraints for better Android compatibility
      const constraints = {
        video: {
          facingMode: 'user'
        }
      };

      let stream;
      try {
        // First attempt with simplified constraints
        stream = await navigator.mediaDevices.getUserMedia(constraints);
      } catch (initialError) {
        logger.error("Initial camera access failed, trying fallback:", initialError);
        // Fallback to simplest possible constraint
        stream = await navigator.mediaDevices.getUserMedia({ video: true });
      }

      // Add video tracks to existing stream or create new one
      if (mediaStreamRef.current) {
        const audioTracks = mediaStreamRef.current.getAudioTracks();

        // Create a new stream with both audio and video
        const combinedStream = new MediaStream([
          ...audioTracks,
          ...stream.getVideoTracks()
        ]);

        // Stop the separate video stream tracks since they're now in the combined stream
        stream.getVideoTracks().forEach(track => {
          // Don't stop it, as it's now in the combined stream
        });

        mediaStreamRef.current = combinedStream;
      } else {
        mediaStreamRef.current = stream;
      }

      // Set the stream to the video element
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStreamRef.current;
      }

      setPermissionStatus(prev => ({ ...prev, camera: 'granted' }));
      return true;
    } catch (error) {
      logger.error("Camera permission error:", error);
      setPermissionStatus(prev => ({ ...prev, camera: 'denied' }));
      return false;
    }
  };

  // Check screen sharing permission
  const checkScreenSharePermission = async () => {
    try {
      setPermissionStatus(prev => ({ ...prev, screenShare: 'checking' }));

      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: false
      });

      // Store screen share stream separately
      const screenStream = stream;

      // Stop the screen share immediately after confirming it works
      screenStream.getTracks().forEach(track => track.stop());

      setPermissionStatus(prev => ({ ...prev, screenShare: 'granted' }));
      return true;
    } catch (error) {
      logger.error("Screen sharing permission error:", error);
      setPermissionStatus(prev => ({ ...prev, screenShare: 'denied' }));
      return false;
    }
  };

  // Handle next step
  const handleNextStep = async () => {


    // if every check is passed, join the interview
    if (permissionStatus.microphone === 'granted' &&
      permissionStatus.camera === 'granted' && currentStep === TOTAL_STEPS - 1) {
      handleJoin();
      return;
    }
    // If we're in screen sharing mode, complete with screen sharing status
    if (startWithScreenSharing) {
      if (onComplete) {
        onComplete({
          screenShare: permissionStatus.screenShare === 'granted'
        });
      }
      return;
    }

    // Regular flow for start-interview page
    if (currentStep === TOTAL_STEPS - 1) {
      // All checks completed
      if (onComplete) {
        onComplete({
          microphone: permissionStatus.microphone === 'granted',
          camera: permissionStatus.camera === 'granted',
          faceDetection: permissionStatus.faceDetection === 'granted',
          guidelinesAcknowledged: guidelinesAcknowledged
        });
      }
      return;
    }

    setCurrentStep(prev => prev + 1);
  };

  // Handle previous step
  const handlePrevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  // Run checks when step changes
  useEffect(() => {
    const runStepCheck = async () => {
      const currentStepId = steps[currentStep]?.id;
      
      switch (currentStepId) {
        case 'welcome':
          // Welcome step, no checks needed
          break;
        case 'microphone':
          await checkMicrophonePermission();
          break;
        case 'camera':
          await checkCameraPermission();
          break;
        case 'screenShare':
          // Screen sharing is manual
          break;
        case 'guidelines':
          // Guidelines step, no checks needed
          break;
      }
    };

    runStepCheck();
  }, [currentStep, steps]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Stop all media tracks
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(track => track.stop());
      }

      // Close audio context
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  // Render content based on current step
  const renderContent = () => {
    // If we're starting with screen sharing, only show the screen sharing step
    if (startWithScreenSharing) {
      return (
        <ScreenShareStep 
          permissionStatus={permissionStatus} 
          checkScreenSharePermission={checkScreenSharePermission}
          isMobile={isMobile}
        />
      );
    }

    // Get current step from configuration
    const currentStepConfig = steps[currentStep];
    if (!currentStepConfig) return null;
    
    const StepComponent = currentStepConfig.component;
    
    // Render the appropriate component with needed props
    switch (currentStepConfig.id) {
      case 'welcome':
        return <StepComponent isMobile={isMobile} />;
        
      case 'microphone':
        return (
          <StepComponent 
            permissionStatus={permissionStatus} 
            checkMicrophonePermission={checkMicrophonePermission} 
            audioData={audioData}
            isMobile={isMobile}
          />
        );
        
      case 'camera':
        return (
          <StepComponent 
            permissionStatus={permissionStatus} 
            setPermissionStatus={setPermissionStatus} 
            checkCameraPermission={checkCameraPermission}
            isMobile={isMobile}
          />
        );
        
      case 'botInteraction':
        return (
          <StepComponent 
            interviewId={interviewId}
            setPermissionStatus={setPermissionStatus}
            isMobile={isMobile}
          />
        );
        
      case 'guidelines':
        return (
          <StepComponent 
            guidelinesAcknowledged={guidelinesAcknowledged} 
            setGuidelinesAcknowledged={setGuidelinesAcknowledged}
            isMobile={isMobile}
          />
        );
        
      case 'screenShare':
        return (
          <StepComponent 
            permissionStatus={permissionStatus} 
            checkScreenSharePermission={checkScreenSharePermission}
            isMobile={isMobile}
          />
        );
        
      default:
        return <StepComponent isMobile={isMobile} />;
    }
  };

  // Calculate if next button should be enabled
  const isNextEnabled = () => {
    if(interviewStatus?.jobopening_status != 'OPEN') {
      return false;
    }
    // If we're starting with screen sharing, we only have one step
    if (startWithScreenSharing) {
      return permissionStatus.screenShare === 'granted';
    }

    const currentStepId = steps[currentStep]?.id;
    
    switch (currentStepId) {
      case 'welcome':
        return true; // Welcome screen, always enabled
      case 'microphone':
        return permissionStatus.microphone === 'granted';
      case 'camera':
        // Allow proceeding if face detection is granted or warning (multiple faces)
        // Also allow proceeding after waiting a reasonable time even if no face is detected
        return import.meta.env.VITE_APP_ENV === 'dev' || permissionStatus.camera === 'granted' &&
          (permissionStatus.faceDetection === 'granted' ||
            permissionStatus.faceDetection === 'warning');
      case 'botInteraction':
        // Bot interaction step
        return permissionStatus.botInteraction === 'granted';
      case 'guidelines':
        return guidelinesAcknowledged; // Only enabled if guidelines are acknowledged
      case 'screenShare':
        // if screen share test is passed then enable next button
        return permissionStatus.screenShare === 'granted';
      default:
        return false;
    }
  };

  // Calculate if all permissions are granted
  const allPermissionsGranted =
    permissionStatus.microphone === 'granted' &&
    permissionStatus.camera === 'granted' &&
    permissionStatus.faceDetection === 'granted' &&
    guidelinesAcknowledged;

  return (
    <div className={`w-full h-full flex items-center justify-center font-sans ${className}`}>
      <div className="relative w-full h-full bg-white rounded-xl shadow-md overflow-hidden flex flex-col">
        {/* Main content area with fixed height */}
        <div className="flex flex-col h-full">
          {/* Fixed header with step indicator */}
          <div className="px-6 pt-6 pb-2">
            {!startWithScreenSharing && (
              <StepIndicator currentStep={currentStep} totalSteps={TOTAL_STEPS} />
            )}
          </div>

          {/* Scrollable content area that takes remaining height */}
          <div className="flex-1 p-0 px-2 overflow-y-auto pb-14">
            {renderContent()}
          </div>

          {/* Sticky footer with navigation buttons */}
          <div className="fixed bottom-0 left-0 right-0 flex justify-between items-center px-2 sm:px-4 py-2 sm:py-3 border-t border-gray-200 bg-white shadow-md z-10 w-full">
            {!startWithScreenSharing && currentStep > 0 ? (
              <button
                className="px-3 py-2 flex items-center justify-center text-xs sm:text-sm font-medium text-gray-700 rounded-md shadow-sm focus:outline-none bg-gray-100 hover:bg-gray-200 border border-gray-200"
                onClick={handlePrevStep}
              >
                <ArrowLeft size={14} className="mr-1" />
                Back
              </button>
            ) : (
              <div className="w-[70px]">{/* Empty div to maintain flex spacing */}</div>
            )}

            <button
              className={`px-3 py-2 flex items-center justify-center min-w-[90px] sm:min-w-[100px] text-xs sm:text-sm font-medium text-white rounded-md focus:outline-none shadow-sm ${!isNextEnabled() ? 'bg-gray-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'}`}
              onClick={isNextEnabled() ? handleNextStep : undefined}
              disabled={!isNextEnabled()}
            >
              {isActiveLoader ? 
                <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-3 border-t-transparent border-white"></div> :
                <>
                  {startWithScreenSharing 
                    ? 'Complete' 
                    : currentStep === TOTAL_STEPS - 1 
                      ? 'Start Interview' 
                      : 'Next'
                  }
                  {!startWithScreenSharing && currentStep !== TOTAL_STEPS - 1 && !isNextEnabled() 
                    ? null 
                    : !isActiveLoader 
                      ? <ArrowRight size={14} className="ml-1" /> 
                      : null
                  }
                </>
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PermissionCheck;